import {Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import React from 'react'

import type {SsoOrganization} from '../../types'
import {DEFAULT_COOKIE_EXPIRATION_DAYS, DISMISSAL_DURATION_OPTIONS, getDefaultDurationIndex} from './constants'
import {DismissalSummary} from './DismissalSummary'
import {DurationMenu} from './DurationMenu'

export interface ConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (durationDays: number) => void
  ssoOrgs: SsoOrganization[]
}

export function ConfirmationDialog({isOpen, onClose, onConfirm, ssoOrgs}: ConfirmationDialogProps) {
  const [selectedDurationIndex, setSelectedDurationIndex] = React.useState(getDefaultDurationIndex)

  // Reset to default when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedDurationIndex(getDefaultDurationIndex())
    }
  }, [isOpen])

  const handleConfirm = () => {
    const selectedDuration = DISMISSAL_DURATION_OPTIONS[selectedDurationIndex]
    onConfirm(selectedDuration?.days ?? DEFAULT_COOKIE_EXPIRATION_DAYS)
  }

  if (!isOpen) return null

  return (
    <Dialog
      title="Dismiss SSO Banner"
      width="medium"
      height="auto"
      onClose={onClose}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onClose},
        {
          buttonType: 'primary',
          content: 'Confirm',
          onClick: handleConfirm,
        },
      ]}
    >
      <DismissalSummary ssoOrgs={ssoOrgs} />
      <DurationMenu selectedDurationIndex={selectedDurationIndex} onDurationChange={setSelectedDurationIndex} />
      <p>
        To sign into any of your SSO organizations, visit the{' '}
        <Link href="/settings/organizations" inline>
          SSO settings page.
        </Link>
      </p>
    </Dialog>
  )
}

try{ ConfirmationDialog.displayName ||= 'ConfirmationDialog' } catch {}